import React from "react";
import { Button, Modal, ModalFooter } from "@telluscompany/design-system";
import { useLocalization } from "@i18n";
import { GeneralDashboardTenantSummary } from "../../api/generalDashboard/generalDashboard.types";
import { formatGeneralDisplayValue } from "../../utils/formatGeneralDisplayValue";
import {
  GeneralDashboardModalDetailField,
  GeneralDashboardModalSection,
} from "../GeneralDashboardModalFields";

interface GeneralTenantDetailsModalProps {
  open: boolean;
  onClose: () => void;
  data: GeneralDashboardTenantSummary | null;
}

export const GeneralTenantDetailsModal: React.FC<GeneralTenantDetailsModalProps> = ({
  open,
  onClose,
  data,
}) => {
  const { translate } = useLocalization();
  const fallback = translate("admin.complexes.details.notProvided");

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={translate("general.dashboard.tenantSummary.modalTitle")}
      content={
        <div className="general-dashboard-modal-body general-dashboard-modal-body--narrow">
          <GeneralDashboardModalSection
            title={translate("management.clients.form.section.generalData")}
          >
            <div className="general-dashboard-modal-field-grid">
              <GeneralDashboardModalDetailField
                label={translate("management.clients.form.label.company")}
                value={data?.name}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("management.clients.form.label.tradeName")}
                value={data?.fantasyName}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("management.clients.form.label.cnpj")}
                value={data?.cnpj}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("management.clients.form.label.municipalRegistration")}
                value={data?.municipalRegistration}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("management.clients.form.label.companyPhone")}
                value={data?.phone}
                fallback={fallback}
              />
            </div>
          </GeneralDashboardModalSection>

          <GeneralDashboardModalSection
            title={translate("management.clients.form.section.responsible")}
          >
            {data?.responsible ? (
              <div className="general-dashboard-modal-field-grid">
                <GeneralDashboardModalDetailField
                  label={translate("management.clients.form.label.responsibleName")}
                  value={data.responsible.name}
                  fallback={fallback}
                />
                <GeneralDashboardModalDetailField
                  label={translate("management.clients.form.label.responsibleEmail")}
                  value={data.responsible.email}
                  fallback={fallback}
                />
                <GeneralDashboardModalDetailField
                  label={translate("management.clients.form.label.responsiblePhone")}
                  value={data.responsible.phone}
                  fallback={fallback}
                />
              </div>
            ) : (
              <p className="text-sm opacity-70">{fallback}</p>
            )}
          </GeneralDashboardModalSection>

          <GeneralDashboardModalSection
            title={translate("management.clients.form.section.address")}
          >
            {data?.address ? (
              <div className="general-dashboard-modal-field-grid">
                <GeneralDashboardModalDetailField
                  label={translate("management.clients.form.label.zipCode")}
                  value={data.address.zipCode}
                  fallback={fallback}
                />
                <GeneralDashboardModalDetailField
                  label={translate("management.clients.form.label.street")}
                  value={data.address.street}
                  fallback={fallback}
                />
                <GeneralDashboardModalDetailField
                  label={translate("management.clients.form.label.number")}
                  value={data.address.number}
                  fallback={fallback}
                />
                <GeneralDashboardModalDetailField
                  label={translate("management.clients.form.label.complement")}
                  value={data.address.complement}
                  fallback={fallback}
                />
                <GeneralDashboardModalDetailField
                  label={translate("management.clients.form.label.neighborhood")}
                  value={data.address.neighborhood}
                  fallback={fallback}
                />
                <GeneralDashboardModalDetailField
                  label={translate("management.clients.form.label.city")}
                  value={data.address.city}
                  fallback={fallback}
                />
                <GeneralDashboardModalDetailField
                  label={translate("management.clients.form.label.state")}
                  value={formatGeneralDisplayValue(data.address.state)}
                  fallback={fallback}
                />
                <GeneralDashboardModalDetailField
                  label={translate("management.clients.form.label.country")}
                  value={formatGeneralDisplayValue(data.address.country)}
                  fallback={fallback}
                />
              </div>
            ) : (
              <p className="text-sm opacity-70">{fallback}</p>
            )}
          </GeneralDashboardModalSection>

          <ModalFooter
            footer={
              <Button type="button" variant="primary" onClick={onClose}>
                {translate("admin.complexes.button.close")}
              </Button>
            }
          />
        </div>
      }
    />
  );
};
